import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Calender } from '../../../assets/icons/calender.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  SoftWareLogsInterFace,
  SoftWareLogsResponseInterface,
} from '../../../interface/soft-ware-logs-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import {
  disableSoftWareLogsFiltersApplied,
  fetchListSoftWareLogsFilters,
  fetchSoftWareLogs,
  updateSoftWareLogsFilters,
  setSortData,
  setLastUpdatedField,
} from '../../../redux/slice/SoftWareLogsSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../utils/api/station-management';

/**
 * SoftWare Logs
 * @returns {any} The SoftWareLogList component
 */
function SoftWareLogList() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    SoftWareLogs,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
    LastUpdatedFiled,
  } = useAppSelector((state) => state.softWareLogs);
  const { selectedSingleStationPerformanceList } = useAppSelector(
    (state) => state.stationManagement,
  );

  const column = [
    {
      label: t('date'),
      accessorKey: 'created_at' as keyof SoftWareLogsInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC date
       * @param stationActivity
       * @returns
       */
      customData: (stationActivity: SoftWareLogsInterFace) => (
        <p>{ConvertUTCtoDate(stationActivity.created_at)}</p>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof SoftWareLogsInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC time
       * @param stationActivity
       * @returns
       */
      customData: (stationActivityTime: SoftWareLogsInterFace) => (
        <p>{FormatUtcString(ConvertUTCtoHHMM(stationActivityTime.created_at))}</p>
      ),
    },
    {
      label: t('type'),
      accessorKey: 'type' as keyof SoftWareLogsInterFace,
      sortAllow: true,
      /**
       * Converting timestamp to UTC time
       * @param stationActivity
       * @returns
       */
      customData: (stationActivity: SoftWareLogsInterFace) => {
        const type = stationActivity.type?.toLowerCase();

        return (
          <p className={`${type === 'in' ? 'type-in' : 'type-out'}`}>
            {type ? type.charAt(0).toUpperCase() + type.slice(1) : '-'}
          </p>
        );
      },
    },
    {
      label: t('messageType'),
      accessorKey: 'action' as keyof SoftWareLogsInterFace,
    },
    {
      label: t('message'),
      accessorKey: 'message' as keyof SoftWareLogsInterFace,
      columnWidth: 500,
    },
  ];

  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: SoftWareLogsResponseInterface) => {
    if (!loading) {
      dispatch(
        fetchSoftWareLogs({
          ...data,
          chargingPointSn: selectedSingleStationPerformanceList?.id,
          chargingStation: selectedChargingPoint?.id,
        }),
      );
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'created_at' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('type'), accessorKey: 'type' },
    { label: t('actionType'), accessorKey: 'action' },
    { label: t('message'), accessorKey: 'message' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = {
      chargingStation: selectedChargingPoint?.id,
      chargingPointSn: selectedSingleStationPerformanceList?.id,
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    stationManagementApi
      .fetchStationActivityExport(payload)
      .then((response) => {
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: SoftWareLogsInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'created_at':
                return ConvertUTCtoDate(row?.created_at) || 'N/A';
              case 'time':
                return ConvertUTCtoHHMM(row?.created_at) || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-station-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-station-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      sortBy,
      sortField,
    });
  };
  /**
   * Function for sorting table data
   * @param sortData
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField === 'time' ? 'created_at' : sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: pageNumber,
      limit,
      sortBy,
      sortField,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  useEffect(() => {
    if (!selectedSingleStationPerformanceList?.id) return;

    const currentId = selectedSingleStationPerformanceList.id;
    const lastId = sessionStorage.getItem('SoftWareLogsFiltersList'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(
        fetchListSoftWareLogsFilters({
          chargingStation: selectedChargingPoint?.id,
          chargingPointSn: selectedSingleStationPerformanceList?.id,
        }),
      );
    }
    const queryParameters = GetParametersFromCommonFilters(filters);

    if (SoftWareLogs.length === 0 || lastId !== currentId || isFilterUpdated) {
      fetchData({
        ...queryParameters,
        page,
        limit,
      });
      dispatch(disableSoftWareLogsFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('SoftWareLogsFiltersList', currentId);
  }, [selectedChargingPoint?.id, filters, selectedSingleStationPerformanceList]);

  return (
    <div className="soft-ware-logs-list">
      <CustomTable
        version="v2"
        header={column}
        data={SoftWareLogs}
        serialNo
        sizeChangeButtonRequired
        limit={limit}
        page={page}
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        filterListLoading={filterListLoading}
        filters={filters}
        filtersToDispatch={updateSoftWareLogsFilters}
        filterListToDispatch={fetchListSoftWareLogsFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportLoader={exportLoader}
        refreshCurrentPages={handleRefresh}
        handlePageChange={handlePageChange}
        handleLimitChange={handleLimitChange}
        topHeader={false}
        rightTopContent={
          <div className="top-right-content">
            <div className="last-15-mins-button">
              <MyButton
                label={t('last15Mins')}
                buttonType={LastUpdatedFiled.lastMins ? 'primary-outline-none' : 'none'}
                svgIcon={<Calender />}
                onClickFunc={() => {
                  if (selectedChargingPoint) {
                    const newLastMins = !LastUpdatedFiled.lastMins;
                    const now = new Date();
                    const hours = now.getHours();
                    const minutes = now.getMinutes();
                    const ampm = hours >= 12 ? 'PM' : 'AM';
                    const formattedHours = hours % 12 || 12;
                    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
                    dispatch(
                      setLastUpdatedField({
                        lastUpdatedTime: `${formattedHours}:${formattedMinutes} ${ampm}`,
                        lastMins: newLastMins,
                      }),
                    );
                    const queryParameters = GetParametersFromCommonFilters(filters);
                    fetchData({
                      page: DefaultApiLimit.page,
                      limit,
                      ...queryParameters,
                      last15min: newLastMins,
                    });
                  }
                }}
              />
            </div>
          </div>
        }
        leftTopContent={
          <div className="top-right-content">
            <div className="auto-refreshed-time">
              <p>
                {t('lastUpdated')}: {LastUpdatedFiled.lastUpdatedTime}
              </p>
            </div>
          </div>
        }
        sortData={{
          sortBy,
          sortField,
        }}
        handleSortChange={handleSortFunction}
        paginate
        flexDirection
        onHiddenColumnsChange={setHiddenColumns}
      />
    </div>
  );
}

export default SoftWareLogList;
